import { useState, useCallback, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from 'sonner';
import { logger } from "@/lib/logger";
import type { OrderRecapData } from "@/components/OrderRecapCard";

import {
  type ExecutionPlan,
  type VoiceAgentStatus,
  type VoiceMessage,
  type ConfirmationCardData,
  type OrderConfirmation,
  type UseVoiceAgentProps,
  SESSION_TIMEOUT_MS,
} from "@/hooks/voice-agent/types";

import { useAudioPlayback } from "@/hooks/voice-agent/useAudioPlayback";
import { useRecording } from "@/hooks/voice-agent/useRecording";
import { usePlanManagement } from "@/hooks/voice-agent/usePlanManagement";
import { useConversation } from "@/hooks/voice-agent/useConversation";
import { useSendToAgent } from "@/hooks/voice-agent/useSendToAgent";

// Re-export types for external consumers
export type { VoiceAgentStatus, VoiceMessage, ConfirmationCardData };

export function useVoiceAgent({ siteId, onSessionStart, onSessionEnd }: UseVoiceAgentProps) {
  const [status, setStatus] = useState<VoiceAgentStatus>("idle");
  const [pendingExecutionPlan, setPendingExecutionPlan] = useState<ExecutionPlan | null>(null);
  const [confirmations, setConfirmations] = useState<OrderConfirmation[]>([]);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [confirmationCard, setConfirmationCard] = useState<ConfirmationCardData | null>(null);
  const [pendingOrderRecap, setPendingOrderRecap] = useState<OrderRecapData | null>(null);
  const [pendingOrderInsertIndex, setPendingOrderInsertIndex] = useState<number | null>(null);
  const [processingMessage, setProcessingMessage] = useState<string | null>(null);
  const [lastTranscription, setLastTranscription] = useState<string | null>(null);

  // Session control refs - prevent concurrent calls
  const isStartingSessionRef = useRef(false);

  const queryClient = useQueryClient();

  // Conversation persistence sub-hook
  const {
    messages, setMessages,
    conversationId, setConversationId,
    loadConversation, saveMessages,
  } = useConversation(siteId);

  // Audio playback sub-hook
  const {
    currentAudioElement,
    stopSpeaking,
    playAudioMobileSafe,
    speak,
  } = useAudioPlayback(status, setStatus);

  // Send to agent sub-hook
  const sendToAgent = useSendToAgent({
    siteId, conversationId, messages, queryClient,
    saveMessages, speak, playAudioMobileSafe,
    setStatus, setMessages, setLastTranscription,
    setProcessingMessage, setConfirmationCard,
    setPendingOrderRecap, setPendingOrderInsertIndex,
    setPendingExecutionPlan, setConfirmations, setConversationId,
  });

  // Recording sub-hook
  const recording = useRecording(siteId, setStatus, stopSpeaking, sendToAgent, speak, setLastTranscription);

  // Start session - request mic permission and load conversation
  const startSession = useCallback(async () => {
    if (!siteId) {
      toast.error("Erreur", { description: "Sélectionnez un chantier"});
      return false;
    }

    // Prevent concurrent session starts
    if (isStartingSessionRef.current) {
      logger.debug("useVoiceAgent", "Session start already in progress");
      return false;
    }

    isStartingSessionRef.current = true;

    try {
      // Load conversation in background (don't await to avoid blocking)
      // The conversation will be loaded by the useEffect below
      loadConversation();

      // No mic permission check here - let startRecording() handle it
      // This avoids the iOS bug where stopping tracks immediately after getUserMedia
      // corrupts the audio state for subsequent recording calls

      setIsSessionActive(true);
      onSessionStart?.();

      logger.info("useVoiceAgent", "Session started (mic permission deferred to startRecording)");
      return true;

    } catch (error) {
      logger.error("useVoiceAgent", "Session start error", error);
      return false;
    } finally {
      isStartingSessionRef.current = false;
    }
  }, [siteId, loadConversation, onSessionStart]);

  // End session
  const endSession = useCallback(() => {
    // Stop any recording
    if (recording.mediaRecorderRef.current && recording.mediaRecorderRef.current.state !== "inactive") {
      recording.mediaRecorderRef.current.stop();
    }

    // Stop any speech
    stopSpeaking();

    // Clean up stream
    if (recording.streamRef.current) {
      recording.streamRef.current.getTracks().forEach(track => track.stop());
      recording.streamRef.current = null;
    }

    // Save messages
    if (messages.length > 0) {
      saveMessages(messages);
    }

    setIsSessionActive(false);
    setStatus("idle");
    onSessionEnd?.();

    logger.info("useVoiceAgent", "Session ended");
  }, [stopSpeaking, messages, saveMessages, onSessionEnd, recording.mediaRecorderRef, recording.streamRef]);

  // Plan management sub-hook
  const { confirmPlan, cancelPlan, modifyPlan } = usePlanManagement(
    siteId, queryClient, setPendingExecutionPlan, setConfirmations,
  );

  // Dismiss confirmation
  const dismissConfirmation = useCallback((ts: string) => {
    setConfirmations(prev => prev.filter(c => c.ts !== ts));
  }, []);

  // Dismiss confirmation card
  const dismissConfirmationCard = useCallback(() => {
    setConfirmationCard(null);
  }, []);

  // Session timeout - auto-end after 3 minutes of inactivity
  useEffect(() => {
    if (!isSessionActive) return;

    const timeout = setTimeout(() => {
      logger.info("useVoiceAgent", "Session auto-ended due to inactivity");
      endSession();
      toast.success("Session terminée", { description: "L'assistant a été mis en veille après 3 minutes d'inactivité",
      });
    }, SESSION_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [isSessionActive, messages.length, endSession]);

  // Pre-load conversation when siteId changes (optimizes initial load time)
  useEffect(() => {
    if (siteId) {
      loadConversation();
    }
  }, [siteId, loadConversation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      recording.cleanupRecording();
      window.speechSynthesis.cancel();
    };
  }, []);

  // Dismiss pending order recap
  const dismissPendingOrderRecap = useCallback(() => {
    setPendingOrderRecap(null);
    setPendingOrderInsertIndex(null);
  }, []);

  return {
    status,
    messages,
    conversationId,
    isSessionActive,
    pendingExecutionPlan,
    confirmations,
    confirmationCard,
    pendingOrderRecap,
    pendingOrderInsertIndex,
    processingMessage,
    audioLevel: recording.audioLevel,
    lastTranscription,
    mediaStream: recording.mediaStream, // Reactive state instead of ref.current
    currentAudioElement, // For audio visualization during TTS playback
    startSession,
    endSession,
    startRecording: recording.startRecording,
    stopRecording: recording.stopRecording,
    sendTextMessage: sendToAgent,
    confirmPlan,
    cancelPlan,
    modifyPlan,
    dismissConfirmation,
    dismissConfirmationCard,
    dismissPendingOrderRecap,
    stopSpeaking,
    playAudio: playAudioMobileSafe,
  };
}
